/**
 * useCallMode — v3.21-1753
 * =========================
 * Per-user / per-workstation calling-mode toggle for the optional
 * Browser Headset feature.
 *
 * MODES:
 *   • "standard"       (DEFAULT — existing Twilio server-dial flow, unchanged)
 *   • "browser_headset" (opt-in — canonical browser Twilio Device is loaded
 *                        and registered lazily; only when the operator
 *                        clicks "Use Browser Headset")
 *
 * Preference is stored in localStorage under the key
 *   `ptc.callMode.<userId>`
 * so it's naturally scoped to (a) the currently authenticated operator and
 * (b) the current workstation.  There is NO server preference / no mass
 * activation switch.  If a user never toggles the button, their calling
 * behaviour is identical to pre-v3.21-1753.
 */
import { useCallback, useEffect, useState } from 'react';

const STANDARD = 'standard';
const BROWSER = 'browser_headset';

const keyFor = (userId) => `ptc.callMode.${userId || 'anon'}`;

export function useCallMode(userId) {
  const [mode, setMode] = useState(STANDARD);

  useEffect(() => {
    try {
      const v = window.localStorage.getItem(keyFor(userId));
      // Default to STANDARD if nothing is stored.  We intentionally
      // never auto-enable BROWSER mode — that requires an explicit click.
      setMode(v === BROWSER ? BROWSER : STANDARD);
    } catch (_) {
      setMode(STANDARD);
    }
  }, [userId]);

  const setStandard = useCallback(() => {
    try { window.localStorage.setItem(keyFor(userId), STANDARD); } catch (_) {}
    setMode(STANDARD);
  }, [userId]);

  const setBrowserHeadset = useCallback(() => {
    try { window.localStorage.setItem(keyFor(userId), BROWSER); } catch (_) {}
    setMode(BROWSER);
  }, [userId]);

  return {
    mode,
    isStandard: mode === STANDARD,
    isBrowserHeadset: mode === BROWSER,
    setStandard,
    setBrowserHeadset,
  };
}

export const CALL_MODE = { STANDARD, BROWSER };
